import React, { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  MapPinOff,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  UserX,
} from "lucide-react";
import { getDeliveryConfig, updateDeliveryConfig } from "../../api/settings";
import { showToast } from "../../utils/showToast";
import { SectionCard } from "../ui/SectionCard";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { Switch } from "../ui/Switch";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";
import { EmptyState } from "../ui/EmptyState";
import { FieldLabel } from "../ui/FieldLabel";

// ─────────────────────────────────────────────────────────────────────────────
// DeliveryFailedFormConfig — Editor del paso intermedio configurable de entrega
// fallida, por estado (6/7). Componente autocontenido: carga y guarda contra
// GET/PATCH /configuration/delivery, que resuelve el operador dueño (empresa de
// logística o comercio con flota propia) desde el JWT. Se reutiliza tal cual en
// el panel admin (/admin/settings) y en el comercio-flota (/home/configuracion-empresa).
// ─────────────────────────────────────────────────────────────────────────────

// Títulos/subtítulos se pasan por t() al render (convención clave=español).
const STATUSES = [
  {
    status: 6,
    icon: UserX,
    title: "Sin moradores / rechazo del cliente",
    subtitle:
      "Cuando el repartidor marca «Sin moradores» o «El cliente rechaza o desconoce la compra».",
  },
  {
    status: 7,
    icon: MapPinOff,
    title: "Problemas de dirección",
    subtitle: "Cuando el repartidor marca «Dirección incorrecta» o «Dirección inaccesible».",
  },
];

const MODE_OPTIONS = [
  { value: "none", label: "No pedir datos adicionales" },
  { value: "text", label: "Solo texto libre" },
  { value: "options", label: "Solo opciones predefinidas" },
  { value: "options_text", label: "Opciones predefinidas + texto libre" },
];

const emptyEntry = (status) => ({
  status,
  mode: "none",
  required: true,
  minLength: "",
  maxLength: "",
  options: [],
});

// Construye el estado editable a partir del array `failedDeliveryForm` del backend.
const entriesFromConfig = (config) => {
  const form = Array.isArray(config?.failedDeliveryForm) ? config.failedDeliveryForm : [];
  return STATUSES.map(({ status }) => {
    const found = form.find((e) => e?.status === status);
    if (!found) return emptyEntry(status);
    return {
      status,
      mode: found.mode ?? "none",
      required: found.text?.required ?? true,
      minLength: found.text?.minLength != null ? String(found.text.minLength) : "",
      maxLength: found.text?.maxLength != null ? String(found.text.maxLength) : "",
      options: Array.isArray(found.options?.values) ? [...found.options.values] : [],
    };
  });
};

// Los largos son enteros ≥ 0: ignoramos basura y clampeamos negativos al
// tipear, en vez de dejarlos pasar y descartarlos en silencio al guardar.
const cleanLength = (raw) => {
  if (raw === "") return "";
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return "";
  return String(Math.max(0, n));
};

// true cuando el valor tipeado NO va a entrar al payload (buildPayload solo
// incluye enteros > 0) → mostramos un hint visible en lugar de descartarlo mudo.
const willBeDropped = (v) => v !== "" && !(parseInt(v, 10) > 0);

export default function DeliveryFailedFormConfig() {
  const { t } = useTranslation();
  const uid = useId();
  const [entries, setEntries] = useState(() => STATUSES.map((s) => emptyEntry(s.status)));
  const [snapshot, setSnapshot] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Bump para reintentar la carga desde el panel de error.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getDeliveryConfig()
      .then((res) => {
        if (!active) return;
        const next = entriesFromConfig(res?.data ?? res);
        setEntries(next);
        setSnapshot(JSON.stringify(next));
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
        } else {
          // NO renderizamos el formulario con defaults vacíos: con snapshot=''
          // el Guardar quedaba habilitado y PATCHearía un failedDeliveryForm
          // vacío, borrando la configuración real del operador.
          setLoadError(true);
          showToast(t("No se pudo cargar la configuración de entrega fallida"), false);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // `t` fuera de las deps a propósito: su identidad cambia con el idioma y
    // eso refetcheaba la config, descartando ediciones sin aviso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const dirty = useMemo(() => JSON.stringify(entries) !== snapshot, [entries, snapshot]);

  const patchEntry = (status, patch) =>
    setEntries((prev) => prev.map((e) => (e.status === status ? { ...e, ...patch } : e)));

  const setOption = (status, idx, value) =>
    setEntries((prev) =>
      prev.map((e) =>
        e.status === status
          ? { ...e, options: e.options.map((v, i) => (i === idx ? value : v)) }
          : e,
      ),
    );

  const addOption = (status) =>
    setEntries((prev) =>
      prev.map((e) => (e.status === status ? { ...e, options: [...e.options, ""] } : e)),
    );

  const removeOption = (status, idx) =>
    setEntries((prev) =>
      prev.map((e) =>
        e.status === status ? { ...e, options: e.options.filter((_, i) => i !== idx) } : e,
      ),
    );

  // Normaliza el estado editable al contrato del backend; omite estados en 'none'
  // (un estado ausente del array = sin paso intermedio).
  const buildPayload = () => {
    const failedDeliveryForm = [];
    for (const e of entries) {
      if (e.mode === "none") continue;
      const out = { status: e.status, mode: e.mode };
      if (e.mode === "text" || e.mode === "options_text") {
        out.text = { required: e.mode === "text" ? true : !!e.required };
        const minL = parseInt(e.minLength, 10);
        if (Number.isFinite(minL) && minL > 0) out.text.minLength = minL;
        const maxL = parseInt(e.maxLength, 10);
        if (Number.isFinite(maxL) && maxL > 0) out.text.maxLength = maxL;
      }
      if (e.mode === "options" || e.mode === "options_text") {
        out.options = { values: e.options.map((v) => v.trim()).filter(Boolean) };
      }
      failedDeliveryForm.push(out);
    }
    return failedDeliveryForm;
  };

  const validate = (payload) => {
    for (const e of payload) {
      if (e.options && e.options.values.length === 0) {
        return t("Agrega al menos una opción para los estados con opciones predefinidas.");
      }
      if (
        e.text?.minLength != null &&
        e.text?.maxLength != null &&
        e.text.minLength > e.text.maxLength
      ) {
        return t("El largo mínimo no puede ser mayor que el máximo.");
      }
    }
    return null;
  };

  const handleSave = async () => {
    const payload = buildPayload();
    const error = validate(payload);
    if (error) {
      showToast(error, false);
      return;
    }
    setSaving(true);
    try {
      await updateDeliveryConfig({ failedDeliveryForm: payload });
      // Re-sincroniza el snapshot con el estado normalizado que quedó guardado.
      const normalized = entriesFromConfig({ failedDeliveryForm: payload });
      setEntries(normalized);
      setSnapshot(JSON.stringify(normalized));
      showToast(t("Configuración de entrega fallida guardada"), true);
    } catch {
      showToast(t("No se pudo guardar la configuración"), false);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const restored = snapshot ? JSON.parse(snapshot) : [];
    if (restored.length) setEntries(restored);
  };

  if (loading) {
    // Skeleton con la forma real: dos SectionCards (círculo de ícono + título
    // + subtítulo + select) tras el párrafo intro. Nunca spinner centrado.
    return (
      <div className="flex flex-col gap-5" role="status" aria-live="polite">
        <span className="sr-only">{t("Cargando…")}</span>
        <Skeleton className="h-4 w-full max-w-2xl" />
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border bg-bg-muted p-5 space-y-4">
            <div className="flex items-start gap-3">
              <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <Skeleton className="h-4 w-64 max-w-full" />
                <Skeleton className="h-3 w-96 max-w-full" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-10 w-full max-w-md rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="rounded-lg border border-border bg-bg-muted p-4 text-sm text-fg-muted">
        {t(
          "Tu usuario no pertenece a un operador (empresa de logística o comercio con flota propia) que pueda configurar la entrega fallida.",
        )}
      </div>
    );
  }

  if (loadError) {
    // Panel de error con reintento en lugar del formulario editable: evita el
    // footgun de guardar defaults vacíos encima de la configuración real.
    return (
      <div className="rounded-lg border border-border bg-bg-muted" role="alert">
        <EmptyState
          icon={<AlertTriangle />}
          title={t("No se pudo cargar la configuración de entrega fallida")}
          description={t(
            "Para proteger la configuración guardada, el formulario no se muestra hasta poder cargarla.",
          )}
          action={
            <Button
              variant="outline"
              iconLeft={<RefreshCw size={16} aria-hidden />}
              onClick={() => setReloadKey((k) => k + 1)}
            >
              {t("Reintentar")}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-fg-muted">
        {t(
          "Define qué información adicional debe registrar el repartidor cuando una entrega falla, según el motivo. Con «No pedir datos adicionales», el repartidor pasa directo a la evidencia fotográfica.",
        )}
      </p>

      {STATUSES.map((meta) => {
        const e = entries.find((x) => x.status === meta.status);
        if (!e) return null;
        const Icon = meta.icon;
        const needsText = e.mode === "text" || e.mode === "options_text";
        const needsOptions = e.mode === "options" || e.mode === "options_text";
        const optionsLabelId = `${uid}-options-label-${meta.status}`;
        const optionsErrorId = `${uid}-options-error-${meta.status}`;
        const switchId = `${uid}-required-${meta.status}`;
        const switchNoteId = `${uid}-required-note-${meta.status}`;
        const minId = `${uid}-min-${meta.status}`;
        const maxId = `${uid}-max-${meta.status}`;
        const minDropped = willBeDropped(e.minLength);
        const maxDropped = willBeDropped(e.maxLength);
        return (
          <SectionCard
            key={meta.status}
            icon={<Icon className="w-4 h-4" />}
            title={t(meta.title)}
            subtitle={t(meta.subtitle)}
            bodyClassName="flex flex-col gap-4"
          >
            <div className="sm:max-w-md">
              <Select
                label={t("¿Qué pedir?")}
                options={MODE_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
                value={e.mode}
                onChange={(ev) => patchEntry(meta.status, { mode: ev.target.value })}
                disabled={saving}
              />
            </div>

            {needsOptions && (
              <div
                role="group"
                aria-labelledby={optionsLabelId}
                aria-describedby={e.options.length === 0 ? optionsErrorId : undefined}
                className="flex flex-col gap-2"
              >
                <div className="space-y-0.5">
                  <FieldLabel id={optionsLabelId}>{t("Opciones predefinidas")}</FieldLabel>
                  <p className="text-xs text-fg-muted">
                    {t("El repartidor podrá seleccionar una o varias.")}
                  </p>
                </div>
                {e.options.map((value, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={value}
                      placeholder={t("Texto de la opción")}
                      aria-label={t("Opción {{num}}", { num: idx + 1 })}
                      onChange={(ev) => setOption(meta.status, idx, ev.target.value)}
                      disabled={saving}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t("Eliminar opción")}
                      onClick={() => removeOption(meta.status, idx)}
                      disabled={saving}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
                <div aria-live="polite">
                  {e.options.length === 0 && (
                    <p id={optionsErrorId} className="text-xs text-status-error">
                      {t("Agrega al menos una opción.")}
                    </p>
                  )}
                </div>
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconLeft={<Plus size={16} />}
                    onClick={() => addOption(meta.status)}
                    disabled={saving}
                  >
                    {t("Agregar opción")}
                  </Button>
                </div>
              </div>
            )}

            {needsText && (
              <div className="flex flex-col gap-3">
                <div className="space-y-1.5">
                  <Switch
                    id={switchId}
                    checked={e.mode === "text" ? true : !!e.required}
                    onChange={(next) => patchEntry(meta.status, { required: next })}
                    disabled={saving || e.mode === "text"}
                    label={t("Texto obligatorio")}
                    aria-describedby={e.mode === "text" ? switchNoteId : undefined}
                  />
                  {e.mode === "text" && (
                    <p id={switchNoteId} className="text-xs text-fg-muted">
                      {t("En modo «Solo texto libre» el texto siempre es obligatorio.")}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor={minId}>{t("Largo mínimo")}</FieldLabel>
                    <Input
                      id={minId}
                      type="number"
                      min={0}
                      inputMode="numeric"
                      value={e.minLength}
                      placeholder={t("Sin mínimo")}
                      onChange={(ev) =>
                        patchEntry(meta.status, { minLength: cleanLength(ev.target.value) })
                      }
                      disabled={saving}
                      aria-describedby={minDropped ? `${minId}-hint` : undefined}
                    />
                    <div aria-live="polite">
                      {minDropped && (
                        <p id={`${minId}-hint`} className="text-xs text-status-warning">
                          {t("Se ignora al guardar: debe ser un número mayor que 0.")}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <FieldLabel htmlFor={maxId}>{t("Largo máximo")}</FieldLabel>
                    <Input
                      id={maxId}
                      type="number"
                      min={1}
                      inputMode="numeric"
                      value={e.maxLength}
                      placeholder={t("Por defecto 500")}
                      onChange={(ev) =>
                        patchEntry(meta.status, { maxLength: cleanLength(ev.target.value) })
                      }
                      disabled={saving}
                      aria-describedby={maxDropped ? `${maxId}-hint` : undefined}
                    />
                    <div aria-live="polite">
                      {maxDropped && (
                        <p id={`${maxId}-hint`} className="text-xs text-status-warning">
                          {t("Se ignora al guardar: debe ser un número mayor que 0.")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>
        );
      })}

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
          {t("Descartar cambios")}
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!dirty || saving}
          iconLeft={!saving ? <Save size={16} /> : undefined}
        >
          {saving ? t("Guardando…") : t("Guardar cambios")}
        </Button>
      </div>
    </div>
  );
}
